import { type FC, type ReactNode } from "react";
import { useMatch } from "react-router-dom";

import { AppShell, Burger, useMantineTheme } from "@mantine/core";
import { useDisclosure, useMediaQuery } from "@mantine/hooks";
import { useIntl } from "react-intl";
import { useRecoilValue } from "recoil";

import { useFeatureGates } from "../../cof-feature-gates/hooks";
import { GlobalNav } from "../../global-nav/components/GlobalNav";
import { CofConfigId } from "../../gql-generated/types";
import { LeftNavigationV3 } from "../../layout/components/LeftNavigationV3";
import { TopNavigation } from "../../layout/components/TopNavigation";
import { UserProfileDropdownContainer } from "../../membership/components";
import { LensContainersDropdownV3 } from "../../membership/components/lens-containers-dropdown";
import { Flexbox } from "../../shared/components";
import { useOrganizationsV2 } from "../../shared/hooks/useOrganizationsV2";
import { defaultOrganizationContainerSelector } from "../recoil/organizations";
import { AbsoluteRoute } from "../routes/absoluteRoutes";
import { usePageViewBlizzardEvent } from "../routes/hooks/usePageViewBlizzardEvent";

import { appShell, navbar } from "./AppLayout.module.css";
import { messages } from "./messages.generated";

interface Props {
    children: ReactNode;
}

const BREAKPOINT = "md";

export const AppLayout: FC<Props> = ({ children }) => {
    const defaultOrgV1 = useRecoilValue(defaultOrganizationContainerSelector);
    const submitPath = useMatch({ path: AbsoluteRoute.SUBMIT, end: false });
    const lensStudioRedirectPath = useMatch({ path: AbsoluteRoute.LENS_STUDIO_REDIRECT, end: true });
    const createOrgLensFolderPath = useMatch({ path: AbsoluteRoute.CREATE_ORG_LENS_FOLDER, end: false });

    const { configs } = useFeatureGates();
    const globalNavEnabled = configs[CofConfigId.MyLensesGlobalNav].enabled;

    const isMyLensesOrgsV2Enabled = configs[CofConfigId.MyLensesOrgsV2].enabled;
    const { defaultOrganizationContainer: defaultOrgV2 } = useOrganizationsV2();

    const defaultOrg = isMyLensesOrgsV2Enabled ? defaultOrgV2 : defaultOrgV1;

    const showContainersDropdown = !!defaultOrg;

    const showSidebar = !submitPath && !lensStudioRedirectPath && !createOrgLensFolderPath;

    usePageViewBlizzardEvent();
    const [opened, { toggle, close }] = useDisclosure();
    const theme = useMantineTheme();
    const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints[BREAKPOINT]})`);
    const intl = useIntl();

    return (
        <>
            <AppShell
                header={{ height: 50 }}
                navbar={{
                    // if this value is updated, also update the
                    // navbar-max-width value in AppLayout.module.css
                    width: showSidebar ? 260 : 0,
                    breakpoint: BREAKPOINT,
                    collapsed: { mobile: !opened },
                }}
                padding="md"
                className={appShell}
                data-navbar-expanded={opened || undefined}
                data-is-mobile={isMobile || undefined}
            >
                <AppShell.Header>
                    <TopNavigation
                        leftContent={
                            showSidebar && (
                                <>
                                    <Burger
                                        opened={opened}
                                        onClick={toggle}
                                        size="sm"
                                        hiddenFrom="md"
                                        aria-label={intl.formatMessage(
                                            opened
                                                ? messages.closeNavigationMenuAriaText
                                                : messages.openNavigationMenuAriaText,
                                        )}
                                    />
                                    {globalNavEnabled ? <GlobalNav /> : null}
                                </>
                            )
                        }
                    >
                        <Flexbox alignItems="center">
                            {showContainersDropdown && <LensContainersDropdownV3 />}
                            <UserProfileDropdownContainer />
                        </Flexbox>
                    </TopNavigation>
                </AppShell.Header>

                {showSidebar && (
                    <AppShell.Navbar className={navbar} data-testid="navigation-menu">
                        <LeftNavigationV3 closeMenu={close} />
                    </AppShell.Navbar>
                )}

                <AppShell.Main
                    onClick={() => {
                        if (opened) {
                            toggle();
                        }
                    }}
                >
                    {children}
                </AppShell.Main>
            </AppShell>
        </>
    );
};
